import React, { FC, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ServicesCartContainer } from '@components/organisms/ServicesCartContainer/ServicesCartContainer';
import { useData } from '@hooks/useData';
import { useGetServicesCartTotals } from '@services/useGetServicesCartTotals';
import { useServicesCart } from '@hooks/useServicesCart';
import { Card } from '@components/atoms/Card';
import { ConfirmServiceCard } from '@components/molecules/ConfirmServiceCard';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { formatDateToLongLocalized, formatPhoneNumber, formatPrice } from '@utils/format';
import { useCreateReservations } from '@services/useCreateReservations';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useRedirectedOnBack } from '@hooks/useRedirectedOnBack';
import { useGetAllPaymentMethods } from '@services/useGetAllPaymentMethods';
import { SITE_URLS } from '@constants';

export const ServicesBookingConfirm: FC = () => {
    const location = useLocation();
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const { request: requestServicesCartTotals } = useGetServicesCartTotals();
    const {
        request: requestCreateReservations,
        loading: loadingCreateReservations,
        errorMessage: errorMessageCreateReservations,
        lastResponseTime: lastCreateReservations,
    } = useCreateReservations();
    const { request: requestAllPaymentMethods } = useGetAllPaymentMethods();
    const { login, servicesCartTotals, createdReservations, allPaymentMethods, setCreatedOrder } = useData();
    const {
        storeUuid,
        services,
        displayWorkerStep,
        worker,
        day,
        timeSlot,
        observations,
        scheduleType,
        paymentMethod,
        paymentPhoneNumber,
        resetServices,
    } = useServicesCart();
    const { t, i18n } = useTranslation();

    const translations = {
        title:
            scheduleType === 'preBook'
                ? t('services-booking-confirm-component.title-book')
                : t('services-booking-confirm-component.title-confirm'),
        nextButton:
            scheduleType === 'preBook'
                ? t('services-booking-confirm-component.next-book.button')
                : t('services-booking-confirm-component.next-pay.button', {
                      price: formatPrice(servicesCartTotals?.data?.total),
                  }),
        workerLabel: t('services-booking-confirm-component.worker-label'),
        dayLabel: t('services-booking-confirm-component.day-label'),
        timeLabel: t('services-booking-confirm-component.time-label'),
        paymentMethodLabel: t('services-booking-confirm-component.payment-method.label'),
    };

    // update translation
    useEffect(() => {
        if (scheduleType === 'bookAndPay') {
            translations.nextButton = t('services-booking-confirm-component.next-pay.button', {
                price: formatPrice(servicesCartTotals?.data?.total),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [servicesCartTotals?.data?.total]);

    // if user accessed this page by going back on browser, redirect to initial merchant detail page
    useRedirectedOnBack(`/${SITE_URLS.merchantDetail.replace(':uuid', `${storeUuid}`)}`);

    const CURRENT_STEP = displayWorkerStep ? 4 : 3;

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            const redirectTo = encodeURIComponent(location.pathname + location.search);
            navigate(`/${SITE_URLS.login}?redirectTo=${redirectTo}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // request totals, when entering page
    useEffect(() => {
        if (storeUuid) {
            requestServicesCartTotals(
                { uuid: storeUuid || '' },
                {
                    services: services
                        ?.filter((service) => service.checked === true && service.quantity > 0)
                        .map((service) => {
                            return { uuid: service.uuid, quantity: service.quantity };
                        }),
                }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeUuid]);

    const handleOnConfirmReservations = () => {
        requestCreateReservations({
            services: services
                ?.filter((item) => item.checked && item.quantity > 0)
                .map((item) => {
                    return { uuid: item.uuid, quantity: item.quantity };
                }),
            worker_uuid: worker?.uuid,
            schedule_date: day || undefined,
            schedule_time: timeSlot || undefined,
            observation: observations || undefined,
            payment_method: paymentMethod || undefined,
            payment_phone_number: paymentPhoneNumber || undefined,
        });
    };

    useEffect(() => {
        if (lastCreateReservations) {
            if (errorMessageCreateReservations) {
                showDefaultNotification({ type: 'error', message: errorMessageCreateReservations });
            } else if (createdReservations !== null) {
                // to know that the last checkout was of a reservation
                setCreatedOrder(null);
                navigate(`/${SITE_URLS.servicesBookingFeedback}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCreateReservations]);

    // call services, if we don't have the information yet
    useEffect(() => {
        !allPaymentMethods && requestAllPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allPaymentMethods, i18n.language]);

    const chosenPaymentMethod = allPaymentMethods?.data?.find((item) => item.uuid === paymentMethod);

    return (
        <ServicesCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Button
                        label={translations.nextButton}
                        mode={ButtonMode.primary}
                        disabled={loadingCreateReservations}
                        onClick={handleOnConfirmReservations}
                    />
                </div>
            }
            displayOnlyTotalValues={true}>
            <>
                {servicesCartTotals?.data?.services?.map((service) => {
                    return (
                        <ConfirmServiceCard
                            key={`service-${service.uuid}`}
                            name={service.name || ''}
                            quantity={service.quantity || 0}
                            price={service.price}
                            duration={service.duration_min}
                        />
                    );
                })}

                <Card classes="p-4">
                    <div className="d-flex flex-column gap-2">
                        {/* worker */}
                        {worker?.name && (
                            <>
                                <Typography fontSize="16" color="dark" fontWeight="900">
                                    <>
                                        <Typography fontSize="16" color="dark" tagVariant={TypographyTagVariants.span}>
                                            {translations.workerLabel}
                                        </Typography>{' '}
                                        <Typography
                                            fontSize="16"
                                            color="dark"
                                            fontWeight="900"
                                            tagVariant={TypographyTagVariants.span}>
                                            {worker?.name}
                                        </Typography>
                                    </>
                                </Typography>
                            </>
                        )}
                        {/* day and time slot */}
                        {day && (
                            <>
                                <Typography fontSize="16" color="dark" fontWeight="900">
                                    <>
                                        <Typography fontSize="16" color="dark" tagVariant={TypographyTagVariants.span}>
                                            {translations.dayLabel}
                                        </Typography>{' '}
                                        <Typography
                                            fontSize="16"
                                            color="dark"
                                            fontWeight="900"
                                            tagVariant={TypographyTagVariants.span}>
                                            {formatDateToLongLocalized(new Date(day), i18n.language)}
                                        </Typography>
                                    </>
                                </Typography>
                                {timeSlot && (
                                    <Typography classes="text-truncate" fontSize="16" color="dark" fontWeight="900">
                                        <>
                                            <Typography
                                                classes="text-truncate"
                                                fontSize="16"
                                                color="dark"
                                                tagVariant={TypographyTagVariants.span}>
                                                {translations.timeLabel}
                                            </Typography>{' '}
                                            <Typography
                                                classes="text-truncate"
                                                fontSize="16"
                                                color="dark"
                                                fontWeight="900"
                                                tagVariant={TypographyTagVariants.span}>
                                                {timeSlot}
                                            </Typography>
                                        </>
                                    </Typography>
                                )}
                            </>
                        )}
                    </div>
                </Card>

                {/* order summary */}
                {chosenPaymentMethod && (
                    <Card classes="p-4">
                        <div className="row">
                            <div className="col-12 col-sm-6">
                                <Typography classes="mt-1 mb-3" fontSize="16" fontWeight="900">
                                    {translations.paymentMethodLabel}
                                </Typography>
                                {chosenPaymentMethod?.image?.src ? (
                                    <img src={chosenPaymentMethod.image.src} alt={chosenPaymentMethod.name} />
                                ) : (
                                    chosenPaymentMethod?.name && <p>{chosenPaymentMethod.name}</p>
                                )}

                                <div className="d-inline-block ms-3">
                                    {chosenPaymentMethod?.method === 'mbway' && formatPhoneNumber(paymentPhoneNumber || '')}
                                </div>
                            </div>
                        </div>
                    </Card>
                )}
            </>
        </ServicesCartContainer>
    );
};
